import { useEffect, useState } from "react";
import {
  CLONABLE_TIERS,
  ROLE_LABELS,
  nodeOf,
  roleKey,
  type ClonableTier,
  type Paginated,
  type RoleChangePreviewDto,
  type UserDto,
} from "@gatiq/shared";
import { Alert, Button, Modal, SelectField, useToast } from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useOrgRoles } from "@/auth/useOrgRoles";

/**
 * Moves a person to another sales role — a sub-distributor promoted to
 * distributor (D-17). Same account and sign-in; their counters move with
 * them. Waits while anyone reports to them.
 */
export function ChangeRoleModal({
  person,
  onClose,
  onChanged,
}: {
  person: UserDto | null;
  onClose: () => void;
  onChanged: (updated: UserDto) => void;
}) {
  const toast = useToast();
  const roles = useOrgRoles();
  const [target, setTarget] = useState("");
  const [parentId, setParentId] = useState("");
  const [preview, setPreview] = useState<RoleChangePreviewDto | null>(null);
  const [managers, setManagers] = useState<UserDto[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const personId = person?.id ?? null;
  useEffect(() => {
    setTarget("");
    setParentId("");
    setFields({});
    setPreview(null);
    if (!personId) return;
    api
      .get<RoleChangePreviewDto>(`/org/sales-reps/${personId}/role-change`)
      .then(setPreview)
      .catch(() => setPreview(null));
  }, [personId]);

  const current = person ? roleKey(person.role, person.level) : "";
  const choices = (roles?.roles ?? []).filter(
    (r) => (CLONABLE_TIERS as readonly string[]).includes(r.base) && r.key !== current,
  );
  const chosen = choices.find((r) => r.key === target) ?? null;
  const managerKey = chosen?.reportsTo ?? null;
  const nameOf = (key: string) => roles?.roles.find((r) => r.key === key)?.name ?? key;

  // Who the new level would report to, when it reports to anyone.
  useEffect(() => {
    setParentId("");
    if (!managerKey) {
      setManagers([]);
      return;
    }
    const { tier, level } = nodeOf(managerKey);
    const query = new URLSearchParams({ role: tier, level: String(level), page: "1", pageSize: "100" });
    api
      .get<Paginated<UserDto>>(`/org/sales-reps?${query.toString()}`)
      .then((res) => setManagers(res.items.filter((m) => m.id !== personId)))
      .catch(() => setManagers([]));
  }, [managerKey, personId]);

  if (!person) return null;
  const first = person.name.split(" ")[0] ?? person.name;
  const blocked = (preview?.reports ?? 0) > 0;

  async function confirm(): Promise<void> {
    if (!chosen || !person) return;
    setSaving(true);
    setFields({});
    try {
      const res = await api.post<{ user: UserDto; countersMoved: number }>(`/org/sales-reps/${person.id}/role`, {
        role: chosen.base,
        level: chosen.level,
        parentId: parentId || undefined,
      });
      toast.success(`${person.name} is now ${chosen.name}.`);
      onChanged(res.user);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Could not change the role.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            disabled={!chosen || blocked || (Boolean(managerKey) && !parentId)}
            loading={saving}
            onClick={() => void confirm()}
          >
            Change role
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={`Change role — ${person.name}`}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          Now: <span className="font-medium text-ink">{nameOf(current)}</span>
          {person.displayId ? <span className="font-mono"> · {person.displayId}</span> : null}
        </p>

        {blocked ? (
          <Alert kind="danger">
            {preview!.reports} {preview!.reports === 1 ? "person reports" : "people report"} to {first}. Move them
            first.
          </Alert>
        ) : null}

        <SelectField
          error={fields.role}
          label="New role"
          onChange={(e) => setTarget(e.target.value)}
          options={choices.map((r) => ({
            value: r.key,
            label: r.name === ROLE_LABELS[r.base as ClonableTier] ? r.name : `${r.name} (${ROLE_LABELS[r.base]})`,
          }))}
          placeholder="Choose a role"
          value={target}
        />

        {managerKey ? (
          <SelectField
            error={fields.parentId}
            hint={managers.length === 0 ? `Add a ${nameOf(managerKey)} first.` : undefined}
            label="Reports to"
            onChange={(e) => setParentId(e.target.value)}
            options={managers.map((m) => ({ value: m.id, label: `${m.name} (${m.displayId ?? ""})` }))}
            placeholder={`Choose a ${nameOf(managerKey)}`}
            required
            value={parentId}
          />
        ) : null}

        {chosen && !blocked ? (
          <ul className="flex flex-col gap-1.5 rounded-md border border-rule bg-surface px-4 py-3 text-sm text-ink">
            {chosen.base !== person.role && preview && preview.counters > 0 ? (
              <li>
                {preview.counters} {preview.counters === 1 ? "counter moves" : "counters move"} with {first}.
              </li>
            ) : null}
            {chosen.base !== person.role ? <li>New {ROLE_LABELS[chosen.base]} ID, same sign-in.</li> : null}
            <li>{first} is signed out once.</li>
          </ul>
        ) : null}
      </div>
    </Modal>
  );
}
